import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Lock, Minus, Plus, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { inr, num, fmtDate } from "@/lib/format";
import { Loading, StatusBadge } from "./kit";
import DayView, { ReconPanel } from "./DayView";

const STEPS = ["Closing Stock", "Collections", "Review & Submit"];

function ClosingCard({ item, value, onChange }) {
  const v = value === "" || value === undefined ? null : Number(value);
  const sold = v === null ? null : item.available - v;
  const set = (n) => onChange(String(Math.max(0, Math.min(item.available, n))));
  return (
    <div className={`rounded-md border bg-white p-4 ${sold !== null && sold < 0 ? "border-red-300" : "border-slate-200"}`} data-testid={`closing-card-${item.sku}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900">{item.name}</p>
          <p className="text-xs text-slate-500">{item.sku} · {inr(item.price)}/pk</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Available</p>
          <p className="font-mono text-xl font-bold" data-testid={`closing-available-${item.sku}`}>{item.available}</p>
        </div>
      </div>
      <p className="mt-1 font-mono text-[11px] text-slate-500">
        Open {item.opening} · Iss {item.issued} · In {item.transfer_in} · Out {item.transfer_out} · Ret {item.returned} · Loss {item.loss}
      </p>
      <div className="mt-3 flex items-center gap-2">
        <Button type="button" variant="outline" className="h-12 w-12 shrink-0" onClick={() => set((v || 0) - 1)} data-testid={`closing-minus-${item.sku}`}><Minus className="h-5 w-5" /></Button>
        <Input type="number" inputMode="numeric" min={0} value={value ?? ""} placeholder="Closing"
          onChange={(e) => onChange(e.target.value)} className="h-12 text-center font-mono text-lg font-bold" data-testid={`closing-input-${item.sku}`} />
        <Button type="button" variant="outline" className="h-12 w-12 shrink-0" onClick={() => set((v || 0) + 1)} data-testid={`closing-plus-${item.sku}`}><Plus className="h-5 w-5" /></Button>
      </div>
      <div className="mt-2 flex justify-between text-sm">
        <span className={sold !== null && sold < 0 ? "font-semibold text-red-600" : "text-slate-600"}>
          {sold === null ? "Enter closing stock" : sold < 0 ? "Closing cannot exceed available" : `Sold: ${sold} pks`}
        </span>
        <span className="font-mono font-semibold" data-testid={`closing-value-${item.sku}`}>{sold > 0 ? inr(sold * item.price) : "₹0"}</span>
      </div>
    </div>
  );
}

function Check({ ok, label, warn }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className={`h-4 w-4 ${warn ? "text-amber-500" : "text-red-600"}`} />}
      <span className={ok ? "text-slate-700" : warn ? "text-amber-700" : "text-red-700"}>{label}</span>
    </li>
  );
}

export default function CloseDayFlow({ sid, date, onDone }) {
  const { data: day, setData } = useFetch(`/day/${sid}/${date}`);
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState({});
  const [money, setMoney] = useState({ cash: "", gpay: "" });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!day) return;
    setClosing(Object.fromEntries(day.items.map((i) => [i.product_id, i.closing ?? ""])));
    setMoney({ cash: day.cash ?? "", gpay: day.gpay ?? "" });
  }, [day]);

  if (!day) return <Loading />;
  if (day.status === "CLOSED") {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="day-locked-banner">
          <Lock className="h-5 w-5 text-emerald-700" />
          <div><p className="font-semibold text-emerald-800">Day {fmtDate(date)} is submitted & locked</p><p className="text-xs text-emerald-700">Only Admin can reopen this day.</p></div>
        </div>
        <DayView day={day} />
      </div>
    );
  }

  const save = async (payload, next) => {
    setBusy(true);
    try {
      const r = await api.put(`/day/${sid}/${date}/draft`, payload);
      setData(r.data);
      if (next !== undefined) setStep(next);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveClosing = (next) => {
    const payload = Object.fromEntries(Object.entries(closing).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => [k, Number(v)]));
    if (Object.keys(payload).length < day.items.length) return toast.error("Please enter closing stock for all products.");
    save({ closing: payload }, next);
  };

  const saveMoney = (next) => {
    if (money.cash === "" || money.gpay === "") return toast.error("Please enter Cash and GPay/UPI (enter 0 if none).");
    save({ cash: Number(money.cash), gpay: Number(money.gpay) }, next);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/day/${sid}/${date}/submit`);
      setData(r.data);
      toast.success("Day closure submitted. Day is now locked.");
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const live = day.items.reduce((a, i) => {
    const c = closing[i.product_id];
    const s = c === "" || c === undefined ? 0 : i.available - Number(c);
    return { packs: a.packs + Math.max(s, 0), value: a.value + Math.max(s, 0) * i.price };
  }, { packs: 0, value: 0 });
  const v = day.validation;
  const preview = { ...day, cash: money.cash === "" ? null : Number(money.cash), gpay: money.gpay === "" ? null : Number(money.gpay) };
  preview.expected_cash = day.totals.sales_value - (preview.gpay || 0) - day.expenses_total;
  preview.difference = preview.expected_cash - (preview.cash || 0);
  preview.receivable = Math.max(preview.difference, 0);
  preview.surplus = Math.max(-preview.difference, 0);

  return (
    <div className="space-y-4" data-testid="close-day-flow">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Business day <span className="font-semibold text-slate-900">{fmtDate(date)}</span></p>
        <StatusBadge status={day.status} testid="close-day-status" />
      </div>
      <div className="grid grid-cols-3 gap-1">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} data-testid={`close-step-${i}`}
            className={`rounded px-2 py-2 text-xs font-semibold transition-colors ${step === i ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-3">
          {day.items.map((i) => (
            <ClosingCard key={i.product_id} item={i} value={closing[i.product_id]} onChange={(val) => setClosing({ ...closing, [i.product_id]: val })} />
          ))}
          <div className="sticky bottom-20 z-10 rounded-md border border-slate-900 bg-slate-900 p-4 text-white lg:bottom-4">
            <div className="flex justify-between text-sm"><span>Packs sold</span><span className="font-mono font-bold" data-testid="live-packs-sold">{num(live.packs)}</span></div>
            <div className="flex justify-between text-sm"><span>Sales value</span><span className="font-mono font-bold" data-testid="live-sales-value">{inr(live.value)}</span></div>
            <Button className="mt-3 h-12 w-full bg-blue-600 text-base hover:bg-blue-700" disabled={busy} onClick={() => saveClosing(1)} data-testid="save-closing-button">Save & Continue</Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4 rounded-md border border-slate-200 bg-white p-4">
          {[["cash", "Cash Collected (₹)"], ["gpay", "GPay / UPI Collected (₹)"]].map(([k, l]) => (
            <label key={k} className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{l}</span>
              <Input type="number" inputMode="decimal" min={0} value={money[k]} onChange={(e) => setMoney({ ...money, [k]: e.target.value })}
                className="mt-1 h-14 font-mono text-xl font-bold" data-testid={`${k}-input`} />
            </label>
          ))}
          <p className="text-xs text-slate-500">Expenses for the day ({inr(day.expenses_total)}) are deducted automatically. Add expenses from the Expenses page.</p>
          <ReconPanel day={preview} />
          <Button className="h-12 w-full bg-blue-600 text-base hover:bg-blue-700" disabled={busy} onClick={() => saveMoney(2)} data-testid="save-collection-button">Save & Review</Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="rounded-md border border-slate-200 bg-white p-4">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-700">Validation</h3>
            <ul className="space-y-1.5" data-testid="close-validation">
              <Check ok={!v.missing_closing} label={v.missing_closing ? `${v.missing_closing} product(s) missing closing stock` : "Closing entered for all products"} />
              <Check ok={!v.negative.length} label={v.negative.length ? `Negative stock: ${v.negative.join(", ")}` : "No negative stock"} />
              <Check ok={v.cash_entered} label={v.cash_entered ? "Cash entered" : "Cash not entered"} />
              <Check ok={v.gpay_entered} label={v.gpay_entered ? "GPay/UPI entered" : "GPay/UPI not entered"} />
              <Check ok warn={false} label={`Expenses reviewed: ${inr(day.expenses_total)}`} />
              <Check ok={!v.pending_transfers && !v.pending_returns} warn label={v.pending_transfers || v.pending_returns ? `${v.pending_transfers} pending transfer(s), ${v.pending_returns} pending return(s) — will not be included today` : "No pending transfers/returns"} />
            </ul>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="closure-summary">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-700">Day Closure Summary</h3>
            {[
              ["Total Packs Sold", num(day.totals.packs_sold)], ["Total Sales Value", inr(day.totals.sales_value)],
              ["Cash", inr(day.cash)], ["GPay/UPI", inr(day.gpay)], ["Expenses", inr(day.expenses_total)],
              ["Expected Cash", inr(day.expected_cash)], ["Actual Cash", inr(day.cash)],
              ["Salesman Receivable", inr(day.receivable)], ["Cash Surplus", inr(day.surplus)],
              ["Stock Shortage / Loss", `${num(day.totals.loss)} pks`], ["Stock Returns", `${num(day.totals.returned)} pks`],
              ["Transfers (In / Out)", `${num(day.totals.transfer_in)} / ${num(day.totals.transfer_out)} pks`],
            ].map(([l, val]) => (
              <div key={l} className="flex justify-between border-b border-slate-100 py-1.5 text-sm last:border-0">
                <span className="text-slate-600">{l}</span><span className="font-mono font-semibold">{val}</span>
              </div>
            ))}
          </div>
          <Button className="h-14 w-full bg-slate-900 text-base font-bold hover:bg-slate-800" disabled={busy} onClick={() => setConfirm(true)} data-testid="submit-day-closure-button">
            <Lock className="mr-2 h-5 w-5" /> SUBMIT DAY CLOSURE
          </Button>
        </div>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit & close {fmtDate(date)}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sales {inr(day.totals.sales_value)} · Receivable {inr(day.receivable)}. After submission the day is locked and cannot be edited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="submit-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={busy} data-testid="submit-confirm-button">SUBMIT & CLOSE DAY</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
